import { useOktaAuth } from '@okta/okta-react';
import { throttle } from 'lodash';
import React, { useState, useEffect, memo } from 'react';
import { SessionTimeout } from '@northerntrust-internal/uxp-react-security';

// Local
import { IOktaSessionTimeout, ISessionObject } from '../utils/types';

/**
 * @param {*} {props: IOktaSessionTimeout}
 * @return {ReactElement}
 */
const OktaSessionTimeout = (props: IOktaSessionTimeout) => {
  const { oktaAuth } = useOktaAuth();

  const {
    timeUntilOpen = 2 * 60,
    refreshFrequency = 2 * 60,
    signOutTime = 0,
    events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'],
    onBeforeOpen = () => undefined,
    onKeepAlive = () => undefined,
    showSeconds = true,
    continueFn = () => undefined,
    logoutFn = () => undefined,
    logoutBtnText = 'Sign Out',
    continueBtnText = 'Stay Signed In',
    header = 'Session Expiring Soon',
    content = 'Your current session will expire soon due to inactivity.'
  } = props;

  const [timeUntilExpiration, setTimeUntilExpiration] = useState<number | null>(null);
  const [timeUntilIdle, setTimeUntilIdle] = useState(0);

  const pluginLogoutFn = () => {
    logoutFn();
    oktaAuth.revokeAccessToken();
    oktaAuth.revokeRefreshToken();
    oktaAuth.session.close();
    oktaAuth.tokenManager.remove('idToken');
    oktaAuth.tokenManager.remove('accessToken');
    oktaAuth.signOut();
  };

  const handleSessionTimeoutModal = (res: ISessionObject) => {
    const currentTime = Math.round(new Date().getTime() / 1000);
    const expireTime = res.expiresAt ? Math.round(new Date(res.expiresAt).getTime() / 1000) : 0;

    const lifeTime = expireTime - currentTime;
    const remainingTimeBeforeModalOpen = lifeTime - timeUntilOpen;

    if (remainingTimeBeforeModalOpen > signOutTime) {
      setTimeUntilIdle(remainingTimeBeforeModalOpen);
    } else {
      pluginLogoutFn();
    }
    setTimeUntilExpiration(lifeTime >= timeUntilOpen ? timeUntilOpen : lifeTime);
  };

  const getSession = () => {
    if (oktaAuth) {
      oktaAuth.session
        .get()
        .then((res: ISessionObject) => {
          if (res?.expiresAt) {
            handleSessionTimeoutModal(res);
          } else {
            console.log('Get Session API call succeeded but no expireAt time', res);
          }
        })
        .catch((e: any) => {
          console.log('Okta Get Session API Call Error: ', e);
          pluginLogoutFn();
        });
    }
  };

  const refreshSession = (forceCall: boolean = false) => {
    const shouldCall = forceCall || !document.querySelector("[aria-label='session timeout popup']");
    if (oktaAuth && shouldCall) {
      onKeepAlive();
      oktaAuth.session
        .refresh()
        .then((res: ISessionObject) => {
          handleSessionTimeoutModal(res);
        })
        .catch((e: any) => {
          console.log('Okta Refresh Session API Call Error: ', e);
          pluginLogoutFn();
        });
    }
  };

  const throttledKeepAlive = throttle(
    () => {
      refreshSession();
    },
    refreshFrequency * 1000,
    { trailing: false, leading: true }
  );

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      throttledKeepAlive();
    }
  };

  const handleListener = (event: Event) => {
    throttledKeepAlive();
  };

  const removeListeners = () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    events
      .filter(event => typeof event === 'string')
      .forEach(event => {
        window.removeEventListener(event, handleListener, true);
      });
  };

  const attachEvents = () => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    events
      .filter(event => typeof event === 'string')
      .forEach(event => {
        window.addEventListener(event, handleListener, true);
      });
  };
  useEffect(() => {
    attachEvents();
    getSession();
    return () => {
      removeListeners();
    };
  }, []);

  return timeUntilExpiration ? (
    <SessionTimeout
      onBeforeOpen={onBeforeOpen}
      timeUntilIdle={timeUntilIdle}
      timeUntilExpiration={timeUntilExpiration}
      showSeconds={showSeconds}
      continueFn={() => {
        continueFn();
        refreshSession(true);
      }}
      logoutFn={pluginLogoutFn}
      logoutBtnText={logoutBtnText}
      continueBtnText={continueBtnText}
      header={header}
      content={content}
    />
  ) : null;
};

export default memo(OktaSessionTimeout);
